/**
 * Combined timber+CNC quote → independent timber + CNC submissions.
 *
 * Staff enter CNC dollars (optional rate × board feet); timber becomes
 * combined − CNC. Creates a package-only CNC placeholder version (no hub
 * release) so supplier portal / staff timeline have a row before any `.3D`.
 */

import type {
  CncQuoteFields,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierOrg,
  TimberSupplierStructureRef,
} from '../types';
import { computeCncQuoteTotal } from './cncVendorExport';
import { isRfqQuoteRejected } from './rfqQuoteRejectionTargets';
import { submissionQuoteTotal } from './rfqQuoteTotal';
import { orgHasCapability, normalizeSupplierOrg } from './vendorHelpers';
import {
  PRE_PORTAL_RELEASE_VERSION_INDEX,
  filterPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
  isQuotePlaceholderPackage,
  isSubmissionApprovedForRelease,
} from './timberRfqPrePortalVersion';
import { rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';

export { isQuotePlaceholderPackage };

/** Supplier-facing version name for CNC packages created from a combined quote. */
export const CNC_QUOTE_PLACEHOLDER_LABEL = 'From quote, no 3D model provided';

/** Portal copy when the CNC carve-out is hidden from the supplier. */
export const CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL = 'Quote on file (no 3D)';

/** Portal tip for locked 3D on a quote-placeholder package. */
export const QUOTE_PLACEHOLDER_VENDOR_PORTAL_NO_3D_HINT =
  'No 3D model was provided with this quote — only the quote PDF is available for this version.';

const SPLIT_TOLERANCE_USD = 0.01;

export type CombinedQuoteSplitMethod = 'rate_x_bf' | 'manual';

export type CombinedQuoteSplitAmounts = {
  combinedTotal: number;
  timberAmount: number;
  cncAmount: number;
};

export type CombinedQuoteSplitInput = {
  combinedTotal: number;
  cncAmount: number;
  timberAmount?: number;
  boardFeet?: number;
  ratePerBdft?: number | null;
  flatFee?: number | null;
};

export type CombinedQuoteSplitValidation =
  | { ok: true; timberAmount: number; cncAmount: number; combinedTotal: number }
  | { ok: false; error: string };

export function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function amountsSumToCombined(
  timberAmount: number,
  cncAmount: number,
  combinedTotal: number,
  tolerance = SPLIT_TOLERANCE_USD,
): boolean {
  return Math.abs(roundMoney(timberAmount) + roundMoney(cncAmount) - roundMoney(combinedTotal)) <= tolerance;
}

export function timberAmountFromCnc(combinedTotal: number, cncAmount: number): number {
  return roundMoney(combinedTotal - cncAmount);
}

export function suggestCncAmountFromRate(
  ratePerBdft: number,
  boardFeet: number,
  flatFee = 0,
): number {
  return computeCncQuoteTotal(flatFee, ratePerBdft, boardFeet);
}

export function validateCombinedQuoteSplit(input: CombinedQuoteSplitInput): CombinedQuoteSplitValidation {
  const combinedTotal = roundMoney(Number(input.combinedTotal));
  const cncAmount = roundMoney(Number(input.cncAmount));
  if (!Number.isFinite(combinedTotal) || combinedTotal <= 0) {
    return { ok: false, error: 'Combined quote total must be a positive amount.' };
  }
  if (!Number.isFinite(cncAmount) || cncAmount < 0) {
    return { ok: false, error: 'Enter a valid CNC amount.' };
  }
  if (cncAmount <= 0) {
    return { ok: false, error: 'CNC amount must be greater than zero.' };
  }
  if (cncAmount >= combinedTotal) {
    return { ok: false, error: 'CNC amount must be less than the combined total.' };
  }
  const timberAmount =
    input.timberAmount != null && Number.isFinite(Number(input.timberAmount))
      ? roundMoney(Number(input.timberAmount))
      : timberAmountFromCnc(combinedTotal, cncAmount);
  if (timberAmount <= 0) {
    return { ok: false, error: 'Timber amount must be greater than zero.' };
  }
  if (!amountsSumToCombined(timberAmount, cncAmount, combinedTotal)) {
    return {
      ok: false,
      error: `Timber + CNC must equal the combined total (${combinedTotal.toFixed(2)}).`,
    };
  }
  return { ok: true, timberAmount, cncAmount, combinedTotal };
}

export function resolveCombinedQuoteSplitMethod(input: {
  ratePerBdft?: number | null;
  boardFeet?: number | null;
}): CombinedQuoteSplitMethod {
  const rate = Number(input.ratePerBdft);
  const bf = Number(input.boardFeet);
  if (Number.isFinite(rate) && rate > 0 && Number.isFinite(bf) && bf > 0) {
    return 'rate_x_bf';
  }
  return 'manual';
}

export function buildCncQuoteFieldsForSplit(input: {
  cncAmount: number;
  ratePerBdft?: number | null;
  flatFee?: number | null;
  boardFeet?: number | null;
}): CncQuoteFields | undefined {
  const rate = Number(input.ratePerBdft);
  const flat = Number(input.flatFee);
  const hasRate = Number.isFinite(rate) && rate > 0;
  const hasFlat = Number.isFinite(flat) && flat > 0;
  if (!hasRate && !hasFlat) {
    return { totalEstimate: roundMoney(input.cncAmount) };
  }
  const flatFee = hasFlat ? flat : 0;
  const ratePerBdft = hasRate ? rate : 0;
  return {
    flatFee: hasFlat ? flatFee : undefined,
    ratePerBdft: hasRate ? ratePerBdft : undefined,
    totalEstimate: roundMoney(input.cncAmount),
  };
}

export function isQuotePlaceholderSubmission(
  sub: Pick<RfqSubmission, 'quotePlaceholder'> | null | undefined,
): boolean {
  return sub?.quotePlaceholder === true;
}

export function hasQuotePlaceholderPackages(packages: RfqPackage[]): boolean {
  return packages.some(isQuotePlaceholderPackage);
}

export function findCncQuotePlaceholderPackageForOrg(
  packages: RfqPackage[],
  projectId: string,
  orgId: string,
): RfqPackage | undefined {
  const scoped = filterPackagesForReleaseSelection(
    packages.filter(
      (p) =>
        p.rfqProjectId === projectId
        && p.supplierOrgId === orgId
        && (p.workType === 'cnc_quote' || !p.workType),
    ),
    PRE_PORTAL_RELEASE_ID,
  );
  return scoped.find(isQuotePlaceholderPackage) ?? scoped[0];
}

export type CombinedQuoteSplitCueArgs = {
  orgId: string;
  org: SupplierOrg | null | undefined;
  timberProject: RfqProject | null | undefined;
  /** Optional — split entry must show even when the deal has no cnc_quote RFQ yet. */
  cncProject: RfqProject | null | undefined;
  timberSubmission: RfqSubmission | null | undefined;
  cncSubmissionsForOrg: RfqSubmission[];
};

/**
 * Soft cue: timber-capable org with a timber quote and no CNC quote yet.
 * Does **not** require an existing cnc_quote RFQ project (Georgian II-style deals).
 *
 * CNC org capability is preferred but not a hard gate: if the org lacks a CNC
 * capability flag we still show the entry so staff can proceed; commit adds the
 * org to the CNC roster (capability is not silently used to hide the button).
 */
export function needsCombinedQuoteSplitCue(args: CombinedQuoteSplitCueArgs): boolean {
  const org = args.org ? normalizeSupplierOrg(args.org) : null;
  if (!org || !orgHasCapability(org, 'timber')) return false;
  if (!args.timberProject?.id) return false;
  const timberRoster = rfqProjectSupplierOrgIds(args.timberProject);
  if (timberRoster.length > 0 && !timberRoster.includes(args.orgId)) return false;
  const timber = args.timberSubmission;
  if (!timber || timber.notBidding || isRfqQuoteRejected(timber)) return false;
  if (timber.linkedCncSubmissionId) return false;
  const total = submissionQuoteTotal(timber);
  if (total == null || total <= 0) return false;
  if (!timber.storagePath && !timber.cncQuote) return false;
  const hasActiveCnc = args.cncSubmissionsForOrg.some(
    (s) => !s.notBidding && !isRfqQuoteRejected(s) && (!!s.storagePath || !!s.cncQuote || !!s.quoteTotal),
  );
  return !hasActiveCnc;
}

export type CanShowCombinedQuoteSplitEntryArgs = CombinedQuoteSplitCueArgs & {
  canApprove: boolean;
  isQuoteApproved?: boolean;
  isPendingRevision?: boolean;
};

/** Full staff UI gate for the Split CNC entry (timber card + CNC empty state). */
export function canShowCombinedQuoteSplitEntry(args: CanShowCombinedQuoteSplitEntryArgs): boolean {
  if (!args.canApprove) return false;
  if (args.isQuoteApproved) return false;
  if (args.isPendingRevision) return false;
  return needsCombinedQuoteSplitCue(args);
}

/** True when the org lacks a CNC capability flag (staff may still split; roster is updated on commit). */
export function combinedQuoteSplitMissingCncCapability(
  org: SupplierOrg | null | undefined,
): boolean {
  if (!org) return true;
  return !orgHasCapability(normalizeSupplierOrg(org), 'cnc');
}

export type CanCommitCombinedQuoteSplitArgs = {
  timberProject: RfqProject;
  timberSubmission: RfqSubmission;
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  selectedReleaseId: string | null;
};

/** Block split when the combined timber quote is already approved (until unapproved). */
export function canCommitCombinedQuoteSplit(
  args: CanCommitCombinedQuoteSplitArgs,
): { ok: true } | { ok: false; error: string } {
  const { timberSubmission: sub } = args;
  if (!sub.id) return { ok: false, error: 'Timber quote is missing an id.' };
  if (sub.linkedCncSubmissionId) {
    return { ok: false, error: 'This quote was already split into a CNC submission.' };
  }
  if (isRfqQuoteRejected(sub)) {
    return { ok: false, error: 'Rejected quotes cannot be split.' };
  }
  if (sub.notBidding) {
    return { ok: false, error: 'Not-bidding rows cannot be split.' };
  }
  const total = submissionQuoteTotal(sub);
  if (total == null || total <= 0) {
    return { ok: false, error: 'Timber quote needs a positive total before split.' };
  }
  if (
    isSubmissionApprovedForRelease(
      args.timberProject,
      args.packages,
      args.submissionsByPackageId,
      args.selectedReleaseId,
      sub.id,
    )
  ) {
    return {
      ok: false,
      error: 'Unapprove this timber quote before splitting CNC out of the combined total.',
    };
  }
  return { ok: true };
}

export type EnsureCncQuoteRfqProjectDeps = {
  createRfqProject: (row: Omit<RfqProject, 'id' | 'createdAt'>) => Promise<string>;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  /**
   * Re-read the deal's cnc_quote RFQ before creating (idempotent retry after a
   * partial failure that already created the project).
   */
  findCncProjectForDeal?: (dealId: string) => RfqProject | null | undefined;
};

export type EnsureCncQuoteRfqProjectArgs = {
  existingCncProject: RfqProject | null | undefined;
  timberProject: RfqProject;
  orgId: string;
  orgName?: string;
  staffUserId: string;
  staffUserName: string;
};

export type EnsureCncQuoteRfqProjectResult = {
  project: RfqProject;
  created: boolean;
};

/**
 * Ensure a cnc_quote RFQ project exists for the deal and includes `orgId` on its roster.
 * Idempotent: never creates a second project when one already exists for the deal.
 */
export async function ensureCncQuoteRfqProject(
  deps: EnsureCncQuoteRfqProjectDeps,
  args: EnsureCncQuoteRfqProjectArgs,
): Promise<EnsureCncQuoteRfqProjectResult> {
  const dealId = (args.timberProject.dealId || args.existingCncProject?.dealId || '').trim();
  if (!dealId) throw new Error('Deal id is required to create a CNC RFQ project.');
  if (!args.orgId) throw new Error('Supplier org id is required.');

  let project =
    args.existingCncProject?.id
      ? args.existingCncProject
      : deps.findCncProjectForDeal?.(dealId) ?? null;

  // Re-check immediately before create so a retry after partial failure does not double-create.
  if (!project?.id) {
    project = deps.findCncProjectForDeal?.(dealId) ?? null;
  }

  if (!project?.id) {
    const id = await deps.createRfqProject({
      dealId,
      dealTitle: args.timberProject.dealTitle || args.existingCncProject?.dealTitle,
      timberProjectId: args.timberProject.timberProjectId,
      timberProjectName: args.timberProject.timberProjectName,
      workType: 'cnc_quote',
      supplierOrgId: args.orgId,
      supplierOrgIds: [args.orgId],
      supplierOrgName: args.orgName,
      status: 'active',
      createdByUid: args.staffUserId,
      createdByName: args.staffUserName,
    });
    return {
      created: true,
      project: {
        id,
        dealId,
        dealTitle: args.timberProject.dealTitle,
        timberProjectId: args.timberProject.timberProjectId,
        timberProjectName: args.timberProject.timberProjectName,
        workType: 'cnc_quote',
        supplierOrgId: args.orgId,
        supplierOrgIds: [args.orgId],
        supplierOrgName: args.orgName,
        status: 'active',
        createdAt: new Date(),
        createdByUid: args.staffUserId,
        createdByName: args.staffUserName,
      },
    };
  }

  const roster = rfqProjectSupplierOrgIds(project);
  if (!roster.includes(args.orgId)) {
    const nextRoster = [...roster, args.orgId];
    await deps.updateRfqProject(project.id, {
      supplierOrgIds: nextRoster,
      supplierOrgId: project.supplierOrgId || args.orgId,
      ...(args.orgName && !project.supplierOrgName ? { supplierOrgName: args.orgName } : {}),
    });
    return {
      created: false,
      project: {
        ...project,
        supplierOrgIds: nextRoster,
        supplierOrgId: project.supplierOrgId || args.orgId,
      },
    };
  }
  return { created: false, project };
}

export type EnsureCncQuotePlaceholderPackageDeps = {
  publishRfqPackage: (input: {
    rfqProjectId: string;
    supplierOrgId: string;
    targetSupplierOrgIds?: string[];
    dealId: string;
    dealTitle?: string;
    workType?: 'cnc_quote';
    label?: string;
    releaseDisplayLabel?: string;
    quotePlaceholder?: boolean;
    releaseVersionIndex?: number;
    cncPackagePayload?: { boardFeet: number; structures?: TimberSupplierStructureRef[] };
    createdByUid: string;
    createdByName: string;
  }) => Promise<string[]>;
};

export async function ensureCncQuotePlaceholderPackage(
  deps: EnsureCncQuotePlaceholderPackageDeps,
  args: {
    cncProject: RfqProject;
    packages: RfqPackage[];
    orgId: string;
    boardFeet: number;
    structures?: TimberSupplierStructureRef[];
    staffUserId: string;
    staffUserName: string;
  },
): Promise<string> {
  const projectId = args.cncProject.id;
  if (!projectId) throw new Error('CNC RFQ project is missing.');

  const existing = findCncQuotePlaceholderPackageForOrg(args.packages, projectId, args.orgId);
  if (existing?.id) return existing.id;

  const bf = Number.isFinite(args.boardFeet) && args.boardFeet > 0 ? roundMoney(args.boardFeet) : 0;
  const ids = await deps.publishRfqPackage({
    rfqProjectId: projectId,
    supplierOrgId: args.orgId,
    targetSupplierOrgIds: [args.orgId],
    dealId: args.cncProject.dealId,
    dealTitle: args.cncProject.dealTitle,
    workType: 'cnc_quote',
    label: CNC_QUOTE_PLACEHOLDER_LABEL,
    releaseDisplayLabel: CNC_QUOTE_PLACEHOLDER_LABEL,
    quotePlaceholder: true,
    releaseVersionIndex: PRE_PORTAL_RELEASE_VERSION_INDEX,
    cncPackagePayload: {
      boardFeet: bf,
      ...(args.structures?.length ? { structures: args.structures } : {}),
    },
    createdByUid: args.staffUserId,
    createdByName: args.staffUserName,
  });
  if (!ids[0]) throw new Error('Could not create CNC quote placeholder package.');
  return ids[0];
}

export type CommitCombinedQuoteSplitDeps = {
  publishRfqPackage: EnsureCncQuotePlaceholderPackageDeps['publishRfqPackage'];
  createRfqSubmission: (
    row: Omit<RfqSubmission, 'id' | 'createdAt'>,
    options?: { createdAt?: Date },
  ) => Promise<string>;
  updateRfqSubmission: (id: string, updates: Partial<RfqSubmission>) => Promise<void>;
  updateRfqProject?: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  /** Required when the deal has no cnc_quote RFQ yet — creates it before split steps. */
  createRfqProject?: EnsureCncQuoteRfqProjectDeps['createRfqProject'];
  findCncProjectForDeal?: EnsureCncQuoteRfqProjectDeps['findCncProjectForDeal'];
  /** Existing CNC submissions for this org (idempotent retry after partial create). */
  existingCncSubmissionsForOrg?: RfqSubmission[];
};

export type CommitCombinedQuoteSplitArgs = {
  timberProject: RfqProject;
  /** May be null/undefined when the deal has no cnc_quote RFQ yet — created on commit. */
  cncProject: RfqProject | null | undefined;
  timberSubmission: RfqSubmission;
  packages: RfqPackage[];
  orgId: string;
  orgName: string;
  combinedTotal: number;
  timberAmount: number;
  cncAmount: number;
  boardFeet: number;
  ratePerBdft?: number | null;
  flatFee?: number | null;
  staffUserId: string;
  staffUserName: string;
};

export type CommitCombinedQuoteSplitResult = {
  cncPackageId: string;
  cncSubmissionId: string;
  timberAmount: number;
  cncAmount: number;
  splitMethod: CombinedQuoteSplitMethod;
  cncProjectId: string;
  createdCncProject?: boolean;
};

function findExistingSplitCncSubmission(
  timberId: string,
  existing: RfqSubmission[] | undefined,
): RfqSubmission | undefined {
  return (existing ?? []).find(
    (s) =>
      s.linkedTimberSubmissionId === timberId
      || (s.quotePlaceholder === true && s.linkedTimberSubmissionId === timberId),
  );
}

export async function commitCombinedQuoteSplit(
  deps: CommitCombinedQuoteSplitDeps,
  args: CommitCombinedQuoteSplitArgs,
): Promise<CommitCombinedQuoteSplitResult> {
  const timberId = args.timberSubmission.id;
  if (!timberId) throw new Error('Timber quote is missing an id.');

  // Idempotent: timber already linked → do not create a second CNC submission/project.
  if (args.timberSubmission.linkedCncSubmissionId) {
    const existingId = args.timberSubmission.linkedCncSubmissionId;
    const existingSub = (deps.existingCncSubmissionsForOrg ?? []).find((s) => s.id === existingId);
    const cncProjectId =
      existingSub?.rfqProjectId
      || args.cncProject?.id
      || deps.findCncProjectForDeal?.(args.timberProject.dealId)?.id
      || '';
    if (!cncProjectId) {
      throw new Error('This quote was already split into a CNC submission.');
    }
    return {
      cncPackageId: existingSub?.rfqPackageId || '',
      cncSubmissionId: existingId,
      timberAmount: roundMoney(Number(args.timberSubmission.quoteTotal) || args.timberAmount),
      cncAmount: roundMoney(
        Number(args.timberSubmission.cncSplitAmount) || args.cncAmount,
      ),
      splitMethod:
        (args.timberSubmission.splitMethod as CombinedQuoteSplitMethod | undefined)
        || resolveCombinedQuoteSplitMethod({
          ratePerBdft: args.ratePerBdft,
          boardFeet: args.boardFeet,
        }),
      cncProjectId,
      createdCncProject: false,
    };
  }

  const validated = validateCombinedQuoteSplit({
    combinedTotal: args.combinedTotal,
    timberAmount: args.timberAmount,
    cncAmount: args.cncAmount,
    boardFeet: args.boardFeet,
    ratePerBdft: args.ratePerBdft,
    flatFee: args.flatFee,
  });
  if (!validated.ok) throw new Error(validated.error);

  const splitMethod = resolveCombinedQuoteSplitMethod({
    ratePerBdft: args.ratePerBdft,
    boardFeet: args.boardFeet,
  });
  const cncQuote = buildCncQuoteFieldsForSplit({
    cncAmount: validated.cncAmount,
    ratePerBdft: args.ratePerBdft,
    flatFee: args.flatFee,
    boardFeet: args.boardFeet,
  });

  let cncProject = args.cncProject ?? null;
  let createdCncProject = false;

  const ensureProjectDeps = (): EnsureCncQuoteRfqProjectDeps => {
    if (!deps.updateRfqProject) {
      throw new Error('updateRfqProject is required to ensure the CNC RFQ project roster.');
    }
    return {
      createRfqProject:
        deps.createRfqProject
        ?? (async () => {
          throw new Error('CNC RFQ project is missing and createRfqProject was not provided.');
        }),
      updateRfqProject: deps.updateRfqProject,
      findCncProjectForDeal: deps.findCncProjectForDeal,
    };
  };

  if (!cncProject?.id) {
    if (!deps.createRfqProject || !deps.updateRfqProject) {
      throw new Error('CNC RFQ project is missing and createRfqProject was not provided.');
    }
    const ensured = await ensureCncQuoteRfqProject(ensureProjectDeps(), {
      existingCncProject: null,
      timberProject: args.timberProject,
      orgId: args.orgId,
      orgName: args.orgName,
      staffUserId: args.staffUserId,
      staffUserName: args.staffUserName,
    });
    cncProject = ensured.project;
    createdCncProject = ensured.created;
  } else if (deps.updateRfqProject) {
    // Existing project: still ensure supplier is on the CNC roster.
    const ensured = await ensureCncQuoteRfqProject(ensureProjectDeps(), {
      existingCncProject: cncProject,
      timberProject: args.timberProject,
      orgId: args.orgId,
      orgName: args.orgName,
      staffUserId: args.staffUserId,
      staffUserName: args.staffUserName,
    });
    cncProject = ensured.project;
  }

  if (!cncProject?.id) throw new Error('CNC RFQ project is missing.');

  const cncPackageId = await ensureCncQuotePlaceholderPackage(deps, {
    cncProject,
    packages: args.packages,
    orgId: args.orgId,
    boardFeet: args.boardFeet,
    structures: args.timberSubmission.quotedStructures,
    staffUserId: args.staffUserId,
    staffUserName: args.staffUserName,
  });

  // Idempotent: CNC submission may already exist from a partial prior attempt.
  const priorCnc = findExistingSplitCncSubmission(timberId, deps.existingCncSubmissionsForOrg);
  let cncSubmissionId = priorCnc?.id ?? '';

  const now = new Date();
  const quoteCreatedAt =
    args.timberSubmission.createdAt instanceof Date ? args.timberSubmission.createdAt : now;

  if (!cncSubmissionId) {
    cncSubmissionId = await deps.createRfqSubmission(
      {
        rfqPackageId: cncPackageId,
        rfqProjectId: cncProject.id,
        supplierOrgId: args.orgId,
        dealId: cncProject.dealId,
        workType: 'cnc_quote',
        fileName: args.timberSubmission.fileName || 'quote.pdf',
        storagePath: args.timberSubmission.storagePath || '',
        downloadUrl: args.timberSubmission.downloadUrl,
        contentType: args.timberSubmission.contentType,
        sizeBytes: args.timberSubmission.sizeBytes,
        quoteTotal: validated.cncAmount,
        cncQuote,
        quotePlaceholder: true,
        linkedTimberSubmissionId: timberId,
        quotedStructures: args.timberSubmission.quotedStructures,
        uploadedByUid: args.staffUserId,
        uploadedByName: args.staffUserName,
        staffManualUpload: true,
        notes: args.timberSubmission.notes,
      },
      { createdAt: quoteCreatedAt },
    );
  }

  await deps.updateRfqSubmission(timberId, {
    quoteTotal: validated.timberAmount,
    combinedQuoteTotal: validated.combinedTotal,
    cncSplitAmount: validated.cncAmount,
    splitAt: now,
    splitByUid: args.staffUserId,
    splitByName: args.staffUserName,
    splitMethod,
    linkedCncSubmissionId: cncSubmissionId,
  });

  if (
    deps.updateRfqProject
    && cncProject.id
    && cncProject.status !== 'quoted'
    && cncProject.status !== 'closed'
  ) {
    await deps.updateRfqProject(cncProject.id, { status: 'quoted' });
  }

  return {
    cncPackageId,
    cncSubmissionId,
    timberAmount: validated.timberAmount,
    cncAmount: validated.cncAmount,
    splitMethod,
    cncProjectId: cncProject.id,
    createdCncProject,
  };
}

/** Prefill board feet for the split modal (CNC invite / project / package). */
export function resolveSplitBoardFeetPrefill(args: {
  cncProject?: RfqProject | null;
  cncPackagesForOrg?: RfqPackage[];
}): number {
  for (const pkg of args.cncPackagesForOrg ?? []) {
    const bf = pkg.cncPackagePayload?.boardFeet;
    if (typeof bf === 'number' && bf > 0) return bf;
  }
  const projectBf = args.cncProject?.boardFeet;
  if (typeof projectBf === 'number' && projectBf > 0) return projectBf;
  return 0;
}
